import { Email, Refresh } from "@mui/icons-material"
import {
  Box,
  CircularProgress,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material"
import { FC, useCallback, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { EmailExpeditor } from "shared/emailExpeditor"
import { EmailExpeditorThingTypeMrn } from "shared/emailExpeditorData"
import { SharedAccessPatternSaEnum } from "shared/sharedAccessPatternsData"
import { selectSaList } from "src/features/things/thingsSlice"
import { useAppSelector } from "../app/hooks"
import { selectMeIsReady } from "../features/me/meSlice"
import { useGetThingsQuery } from "../features/things/thingsApi"
import EmailExpeditorCard from "./EmailExpeditorCard"
import SaLayout from "./SaLayout"
import { Sl2Btn } from "./Sl2Btn"

const cardSx = {
  bgcolor: "white",
  border: "1px solid",
  borderColor: "rgba(0, 0, 0, 0.08)",
  borderRadius: 2,
  boxShadow: "0 1px 2px rgba(35, 66, 133, 0.04)",
}

export const SaEmailExpeditorsPage: FC = () => {
  const { t } = useTranslation()
  const isReady = useAppSelector(selectMeIsReady)

  const emailExpeditors = useAppSelector((state) =>
    selectSaList<EmailExpeditor>(state),
  ).filter((t) => t.thingTypeMrn === EmailExpeditorThingTypeMrn)

  const [formMode, setFormMode] = useState<"create" | "edit">("edit")
  const { isLoading: isLoadingEmailExpeditors, refetch } = useGetThingsQuery({
    params: {
      "ap.name": SharedAccessPatternSaEnum.SaGetEmailExpeditors,
    },
  })

  const { isLoading: isLoadingTenant, refetch: refetchTenant } =
    useGetThingsQuery({
      params: {
        "ap.name": SharedAccessPatternSaEnum.SaGetTenants,
      },
    })

  const refresh = useCallback(() => {
    refetch()
    refetchTenant()
  }, [refetch, refetchTenant])

  const isLoading = isLoadingEmailExpeditors || isLoadingTenant
  const [selectedEmailExpeditorId, setSelectedEmailExpeditorId] =
    useState<string>()
  const selectedEmailExpeditor = useMemo(
    () =>
      emailExpeditors?.find(
        (emailExpeditor) => emailExpeditor.id === selectedEmailExpeditorId,
      ),
    [emailExpeditors, selectedEmailExpeditorId],
  )
  return (
    <SaLayout selectedMenuItemLabelKey="emailExpeditors">
      <Box
        sx={{
          maxWidth: 1400,
          mx: "auto",
          px: { xs: 3, md: 5 },
          py: { xs: 4, md: 5 },
          width: "100%",
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-end"
          flexWrap="wrap"
          gap={2}
          sx={{ mb: 4 }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: "0.72rem",
                fontWeight: 600,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: "primary.main",
              }}
            >
              {t("adminApp")}
            </Typography>
            <Stack direction="row" spacing={1.5} alignItems="baseline">
              <Typography
                variant="h4"
                sx={{
                  mt: 0.5,
                  fontWeight: 700,
                  letterSpacing: "-0.02em",
                  color: "text.primary",
                  lineHeight: 1.15,
                }}
              >
                {t("emailExpeditors")}
              </Typography>
              <Typography
                sx={{
                  color: "text.secondary",
                  fontSize: "1.1rem",
                  fontWeight: 500,
                }}
              >
                {emailExpeditors?.length ?? 0}
              </Typography>
            </Stack>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            {isLoading ? (
              <CircularProgress size={22} />
            ) : (
              <IconButton
                onClick={refresh}
                size="small"
                sx={{
                  bgcolor: "rgba(35, 66, 133, 0.06)",
                  color: "primary.main",
                  "&:hover": { bgcolor: "rgba(35, 66, 133, 0.12)" },
                }}
              >
                <Refresh fontSize="small" />
              </IconButton>
            )}
            <Sl2Btn
              variant="contained"
              disableElevation
              onClick={() => {
                setFormMode("create")
              }}
            >
              {t("add")}
            </Sl2Btn>
          </Stack>
        </Stack>

        {/* Description */}
        <Stack direction="column" spacing={1} sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary">
            {t("emailExpeditorsDescription1")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("emailExpeditorsDescription2")}
          </Typography>
        </Stack>

        <Stack direction="row" spacing={2.5} alignItems="flex-start">
          {/* List card */}
          <Box sx={{ ...cardSx, width: 400, overflow: "hidden" }}>
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              justifyContent="space-between"
              sx={{ px: 2, py: 1.5 }}
            >
              <Typography variant="body1" fontWeight={"bold"}>
                {t("emailExpeditors")}
              </Typography>
              {isLoading ? (
                <CircularProgress size={20} />
              ) : (
                <IconButton
                  onClick={refetch}
                  size="small"
                  sx={{
                    bgcolor: "rgba(35, 66, 133, 0.06)",
                    color: "primary.main",
                    "&:hover": { bgcolor: "rgba(35, 66, 133, 0.12)" },
                  }}
                >
                  <Refresh fontSize="small" />
                </IconButton>
              )}
            </Stack>
            <Divider sx={{ borderColor: "rgba(0, 0, 0, 0.06)" }} />
            <Stack direction="column" spacing={0}>
              {emailExpeditors &&
                emailExpeditors.map((emailExpeditor, i) => {
                  const isSelected =
                    emailExpeditor.id === selectedEmailExpeditorId
                  const isLast = i === emailExpeditors.length - 1
                  return (
                    <MenuItem
                      key={emailExpeditor.id}
                      selected={isSelected}
                      onClick={() => {
                        setSelectedEmailExpeditorId(emailExpeditor.id)
                        setFormMode("edit")
                      }}
                      sx={{
                        display: "block",
                        px: 2,
                        py: 1.25,
                        position: "relative",
                        borderBottom: isLast ? "none" : "1px solid",
                        borderColor: "rgba(0, 0, 0, 0.06)",
                        transition: "background-color 140ms ease",
                        "&:hover": {
                          bgcolor: "rgba(35, 66, 133, 0.04)",
                        },
                        "&.Mui-selected": {
                          bgcolor: "rgba(35, 66, 133, 0.08)",
                          "&:hover": { bgcolor: "rgba(35, 66, 133, 0.12)" },
                          "&::before": {
                            content: '""',
                            position: "absolute",
                            left: 0,
                            top: 8,
                            bottom: 8,
                            width: 3,
                            borderRadius: 2,
                            bgcolor: "primary.main",
                          },
                        },
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "0.9rem",
                          fontWeight: 600,
                          color: isSelected ? "primary.main" : "text.primary",
                          lineHeight: 1.3,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {emailExpeditor.name}
                      </Typography>
                      <Typography
                        sx={{
                          mt: 0.25,
                          fontSize: "0.78rem",
                          fontFamily: "monospace",
                          color: "text.secondary",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {emailExpeditor.fromEmail}
                      </Typography>
                    </MenuItem>
                  )
                })}
            </Stack>
          </Box>
          {!!selectedEmailExpeditor?.id || formMode === "create" ? (
            <Stack
              direction="column"
              spacing={2}
              sx={{
                width: "95%",
                maxWidth: 1000,
              }}
            >
              <EmailExpeditorCard
                emailExpeditor={
                  formMode === "create" ? undefined : selectedEmailExpeditor
                }
                mode={formMode}
                onComplete={refetch}
              />
            </Stack>
          ) : (
            <Stack
              // center the content
              direction="column"
              sx={{
                width: "95%",
                pt: 6,
              }}
              alignItems={"center"}
            >
              <Email style={{ fontSize: "4em", color: "slategray" }} />
              <Typography>{t("pleaseSelectAEmailExpeditor")}</Typography>
            </Stack>
          )}
        </Stack>
      </Box>
    </SaLayout>
  )
}

export default SaEmailExpeditorsPage
